import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import Toast from 'react-native-toast-message'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'
import I18n from 'APP/I18n'
import HabitsActions from 'APP/Redux/HabitsRedux'
import List from 'APP/Components/List'
import ListItem from 'APP/Converse/ListItem'
import { PrimaryButton, TertiaryButton } from 'APP/Components/Buttons'
import {
  createStyles,
  StyledPromptOptionTitle,
  StyledPromptOption,
  StyledHighlightedPromptOption,
  StyledActivityIndicator,
  StyledHighlightedActivityIndicator,
  StyledGuidanceText,
} from './style'
import { useTheme } from 'styled-components/native'
import Typography from 'APP/Converse/Typography'
import TextInput from 'APP/Converse/Inputs/TextInput'

const useHabitPrompt = ({ habit, selectedDay }) => {
  const dispatch = useDispatch()
  const removeHabitPromptState = useSelector((state) => state.habits.removeHabitPrompt)
  const updateHabitPromptState = useSelector((state) => state.habits.updateHabitPrompt)
  const isLoading = updateHabitPromptState.loading || removeHabitPromptState.loading
  const submittingPromptId =
    isLoading && (updateHabitPromptState.promptId || removeHabitPromptState.promptId)

  const removeHabitPrompt = (prompt) =>
    dispatch(HabitsActions.removeHabitPrompt(habit.identifier, prompt, selectedDay))
  const updateHabitPrompt = (prompt) =>
    dispatch(HabitsActions.updateHabitPrompt(habit.identifier, prompt, selectedDay))

  // Error handling
  useEffect(() => {
    if (updateHabitPromptState?.retry) {
      Toast.show({ text1: I18n.t('Habits.updateOrRemovePrompt.error') })
      dispatch(HabitsActions.resetUpdateHabitPrompt())
    }

    if (removeHabitPromptState?.retry) {
      Toast.show({ text1: I18n.t('Habits.updateOrRemovePrompt.error') })
      dispatch(HabitsActions.resetRemoveHabitPrompt())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateHabitPromptState, removeHabitPromptState])

  return {
    submittingPromptId,
    removeHabitPrompt,
    updateHabitPrompt,
  }
}

export const HabitPrompt = ({
  habit,
  showGuidance,
  processing,
  selectedDay,
  goToHabitCustomPrompt,
  testID,
}) => {
  const theme = useTheme()
  const styles = createStyles(theme)
  const { submittingPromptId, removeHabitPrompt, updateHabitPrompt } = useHabitPrompt({
    habit,
    selectedDay,
  })

  const isBuiltInPrompt = (prompt) => prompt.type === 'built-in'

  const isPromptOptionHighlighted = useCallback(
    (promptOption) => {
      if (!habit.prompt || !isBuiltInPrompt(habit.prompt)) {
        return false
      }
      return promptOption.identifier === habit?.prompt?.identifier
    },
    [habit]
  )

  const onPromptOptionPress = useCallback(
    (prompt) => {
      isPromptOptionHighlighted(prompt) ? removeHabitPrompt(prompt) : updateHabitPrompt(prompt)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [habit]
  )

  const listItems = useMemo(() => {
    if (!habit?.prompt_options) {
      return []
    }
    return habit.prompt_options.map((promptOption) => {
      const isLoading = submittingPromptId === promptOption.identifier
      return {
        promptOption,
        props: {
          disabled: processing,
          analyticsName: `${promptOption.identifier}:${promptOption.message['en']}`,
          title: promptOption.message[I18n.baseLocale],
          hideIconComponent: !isLoading,
          IconComponent:
            isLoading &&
            (isPromptOptionHighlighted(promptOption)
              ? StyledHighlightedActivityIndicator
              : StyledActivityIndicator),
        },
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habit, processing, submittingPromptId])

  const renderBoldText = (copy) => {
    const separateCopyByBoldParams = copy.split(/(\[\[.*\]\])/g)

    return (
      <>
        {separateCopyByBoldParams[0]}
        {separateCopyByBoldParams[1] && (
          <Typography variant="bodyNormalBold">
            {separateCopyByBoldParams[1].replace('[[', '').replace(']]', '')}
          </Typography>
        )}
        {separateCopyByBoldParams[2]}
      </>
    )
  }

  return (
    <View style={styles.container} testID={testID}>
      {showGuidance && (
        <View testID="PROMPT_GUIDANCE" style={styles.guidanceBar}>
          <MaterialIcon
            name="check-circle-outline"
            size={theme.metrics.icon.small}
            color={theme.colors.darkText}
          />
          <StyledGuidanceText>
            {renderBoldText(I18n.t('Habits.updateOrRemovePrompt.banner'))}
          </StyledGuidanceText>
        </View>
      )}
      <View style={styles.listWrapper} testID="PROMPTS">
        <List containerStyle={styles.list}>
          <ListItem
            disabled
            containerStyle={styles.titleContainer}
            title={habit.title[I18n.baseLocale] + '...'}
            TitleComponent={StyledPromptOptionTitle}
            hideIconComponent
          />
          {listItems.map(({ props, promptOption }) => {
            const isHighlighted = isPromptOptionHighlighted(promptOption)
            return (
              <ListItem
                {...props}
                containerStyle={{
                  backgroundColor: isHighlighted ? theme.colors.acceptButtonRipple : null,
                }}
                TitleComponent={isHighlighted ? StyledHighlightedPromptOption : StyledPromptOption}
                testID="PROMPT_OPTION"
                key={promptOption.identifier}
                onPress={() => onPromptOptionPress(promptOption)}
              />
            )
          })}
          <ListItem
            disabled={processing}
            analyticsName="custom prompt options"
            containerStyle={{
              backgroundColor:
                habit?.prompt?.type === 'custom' ? theme.colors.acceptButtonRipple : null,
            }}
            title={I18n.t('Habits.updateOrRemovePrompt.otherPromptOption')}
            TitleComponent={
              habit?.prompt?.type === 'custom' ? StyledHighlightedPromptOption : StyledPromptOption
            }
            hideIconComponent
            bottomDivider={false}
            testID="PROMPT_OPTION"
            onPress={() => goToHabitCustomPrompt()}
          />
        </List>
      </View>
    </View>
  )
}

export const HabitCustomPrompt = ({
  habit,
  processing,
  selectedDay,
  maxCustomMessageLength = 30,
  goToHabitPrompt,
  testID,
}) => {
  const theme = useTheme()
  const styles = createStyles(theme)
  const { removeHabitPrompt, updateHabitPrompt } = useHabitPrompt({ habit, selectedDay })
  const [customMessageInputValue, setCustomMessageInputValue] = useState(
    () => habit?.prompt?.custom_message || ''
  )

  const ctaIsDisabled = habit?.prompt?.custom_message
    ? customMessageInputValue === habit?.prompt?.custom_message
    : !customMessageInputValue

  const onSubmit = () => {
    if (!customMessageInputValue && habit?.prompt?.custom_message) {
      removeHabitPrompt(habit.prompt)

      return
    }

    updateHabitPrompt({
      custom_message: customMessageInputValue,
    })
  }

  return (
    <View style={styles.container} testID={testID}>
      <TextInput
        testID="HABIT_TEXT_INPUT"
        value={customMessageInputValue}
        maxLength={maxCustomMessageLength}
        onChangeText={setCustomMessageInputValue}
        placeholder={I18n.t('Habits.updateCustomPrompt.customMessageInputPlaceholder')}
        disabled={processing}
        autoFocus={true}
      />
      <View style={styles.customMessageInputHintContainer}>
        <Typography style={styles.customMessageInputHintText}>
          {customMessageInputValue?.length || 0}/{maxCustomMessageLength}
        </Typography>
      </View>
      <PrimaryButton
        disabled={processing || ctaIsDisabled}
        onPress={onSubmit}
        title={I18n.t('Habits.updateCustomPrompt.save')}
        showActivityIndicator={processing}
        activityIndicatorColor={theme.colors.textInverted}
        style={styles.customMessageSubmitButton}
      />
      <TertiaryButton
        disabled={processing}
        onPress={goToHabitPrompt}
        title={I18n.t('Habits.updateCustomPrompt.viewOptions')}
        style={styles.viewOptionsCta}
      />
    </View>
  )
}
